import React from "react";
import { useLocation } from "react-router-dom";
import { useQueryString } from "hooks/general-hooks";
import { useJSONFetcher } from "@outplayed/json-fetcher";
import { fetchRawApi } from "api/apiHelpers";
import { useValidateQueryParams } from "query_parameters/paramsHelper";
import { useTierListURL, useDuoTierListURL, useCombatURL, getObjectivesURL } from "api/championResearchApi";
import { cleanTierList } from "api/data_parser/tier_list.js";
import { parseDuoTierList } from "api/data_parser/duo_tier_list.js";
import { cleanCombat } from "api/data_parser/combat.js";
import { cleanObjectives } from "api/data_parser/objectives.js";

export function useTierList({ customParams, ssr, skip } = {}) {
  const queryParams = useQueryString();
  const validateQueryParams = useValidateQueryParams();
  const validatedQueryParams = validateQueryParams({
    queryParams: customParams || queryParams,
    page: "tier-list",
    keepDefaultParams: true,
  });

  const url = useTierListURL(validatedQueryParams);
  return useJSONFetcher(url, {
    skip,
    ssr,
    onCompleted: (url, json) => cleanTierList(json),
  });
}

export function useSpecialTierList({ customParams, ssr, skip, skipURL } = {}) {
  const { queueType, buildPageName } = customParams;
  if (!queueType || !buildPageName) return null;

  const queryParams = useQueryString();
  const validateQueryParams = useValidateQueryParams();
  const validatedQueryParams = validateQueryParams({
    queryParams: customParams || queryParams,
    page: buildPageName,
    keepDefaultParams: true,
  });

  const url = useTierListURL(
    {
      ...validatedQueryParams,
      queueType,
    },
    skipURL,
  );
  return useJSONFetcher(url, {
    skip,
    ssr,
    onCompleted: (url, json) => cleanTierList(json),
  });
}

export function useDuoTierList({ ssr, skip } = {}) {
  const queryParams = useQueryString();
  const validateQueryParams = useValidateQueryParams();
  const validatedQueryParams = validateQueryParams({
    queryParams,
    page: "duo-tier-list",
    keepDefaultParams: true,
  });

  const url = useDuoTierListURL(validatedQueryParams);
  return useJSONFetcher(url, {
    skip,
    ssr,
    onCompleted: (url, json) => parseDuoTierList(json),
  });
}

export function useCombatTierList({ ssr, skip } = {}) {
  const queryParams = useQueryString();
  const validateQueryParams = useValidateQueryParams();
  const validatedQueryParams = validateQueryParams({
    queryParams,
    page: "combat",
    keepDefaultParams: true,
  });

  const url = useCombatURL(validatedQueryParams);
  return useJSONFetcher(url, {
    skip,
    ssr,
    onCompleted: (url, json) => cleanCombat(json),
  });
}

// Promise
export function useCombatStats(options) {
  const validateQueryParams = useValidateQueryParams();
  const validatedQueryParams = validateQueryParams({
    queryParams: options,
    page: "combat",
    keepDefaultParams: true,
  });
  const url = useCombatURL(validatedQueryParams);
  return () => {
    return fetchRawApi(url);
  };
}

export function useObjectivesTierList({ ssr, skip } = {}) {
  const queryParams = useQueryString();
  const validateQueryParams = useValidateQueryParams();
  const validatedQueryParams = validateQueryParams({
    queryParams,
    page: "objectives",
    keepDefaultParams: true,
  });

  const url = getObjectivesURL(validatedQueryParams);
  return useJSONFetcher(url, {
    skip,
    ssr,
    onCompleted: (url, json) => cleanObjectives(json),
  });
}

// Promise
export function useObjectives(options) {
  const validateQueryParams = useValidateQueryParams();
  const validatedQueryParams = validateQueryParams({
    queryParams: options,
    page: "objectives",
    keepDefaultParams: true,
  });

  const url = getObjectivesURL(validatedQueryParams);
  return () => {
    return fetchRawApi(url);
  };
}
